import React, { useCallback, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AppLayout from './components/AppLayout';
import DashboardSection from './components/DashboardSection';
import HomeworkTable from './components/HomeworkTable';
import ItemList from './components/ItemList';
import Modal from './components/Modal';
import SummaryTile from './components/SummaryTile';
import { useLanguage } from './contexts/LanguageContext';
import { fetchClassDashboards, fetchDeadline, fetchDeviceSchool, fetchHomework, fetchSubjectGroups, fetchUpcomingEvents } from './services/whiteboardApi';
import { mergeClassDashboards } from './utils/classDashboard';
import {
    addStoredClassTokens,
    clearStoredClassTokens,
    getClassTokensFromUrl,
    getStoredClassTokens,
    removeStoredClassToken,
} from './utils/classTokens';
import { formatBoardDate, getLocalDateString } from './utils/date';
import { buildClassHomeworkTable, buildHomeworkTable } from './utils/tableData';
import {
    clearStoredSchool,
    getStoredBoard,
    getStoredSchool,
    getSubjectSelections,
    replaceSubjectSelections,
    saveSubjectSelection,
    selectedGroupTokens,
    setStoredBoard,
    setStoredSchool,
    uniqueSubjectCatalog,
} from './utils/subjectGroups';

function Home() {

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const [homeworkUrl, setHomeworkUrl] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [homeworkTable, setHomeworkTable] = useState({ tableData: undefined, maxCol: 0, maxRow: 0 });
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [deadlineItems, setDeadlineItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [activeModal, setActiveModal] = useState('');
    const [activeClasses, setActiveClasses] = useState([]);
    const [schoolUid, setSchoolUid] = useState('');
    const [schoolName, setSchoolName] = useState('');
    const [boardId, setBoardId] = useState(getStoredBoard);
    const [boardName, setBoardName] = useState('');
    const [subjectCatalog, setSubjectCatalog] = useState([]);
    const [boardRows, setBoardRows] = useState([]);
    const [subjectSelections, setSubjectSelections] = useState(getSubjectSelections);
    const [today] = useState(getLocalDateString);
    const { language } = useLanguage();
    const selectedSubjectCount = Object.keys(subjectSelections).length;

    const name = searchParams.get("name");

    const fetchDashboardData = useCallback(async (username) => {
        setIsLoading(true);
        setErrorMessage('');
        try {
            const [homeworkData, upcomingData, deadlineData] = await Promise.all([
                fetchHomework(username),
                fetchUpcomingEvents(username),
                fetchDeadline(username, today),
            ]);

            const table = buildHomeworkTable(homeworkData.table);

            setHomeworkTable(table);
            setUpcomingEvents(upcomingData.table || []);
            setDeadlineItems((deadlineData.table || []).map((item) => ({
                ...item,
                deadline: item.deadline || today,
            })));
        } catch (error) {
            console.error('There was a problem with the POST request:', error);
            setErrorMessage('Unable to load your dashboard right now. Please try again later.');
        } finally {
            setIsLoading(false);
        }

    }, [today]);

    const fetchClassData = useCallback(async (
        tokens,
        targetBoardId = getStoredBoard(),
        dashboardRows = [],
        selectedGroups = {},
    ) => {
        setIsLoading(true);
        setErrorMessage('');
        try {
            const { dashboards, failedCount } = await fetchClassDashboards(tokens, today, targetBoardId);
            const data = mergeClassDashboards(dashboards);
            const visibleRows = dashboardRows.length
                ? dashboardRows
                : data.class_tags
                    .filter((classTag) => classTag.subject)
                    .map((classTag) => ({
                        subject: classTag.subject,
                        class_tag_name: classTag.tag_name,
                    }));
            setHomeworkTable(buildClassHomeworkTable(data.assignments, visibleRows, selectedGroups));
            setUpcomingEvents(data.upcoming);
            setDeadlineItems(data.due_today);
            setActiveClasses(data.class_tags);
            if (failedCount) {
                setErrorMessage(`${failedCount} saved teaching-group link could not be loaded. The other groups are shown below.`);
            }
        } catch (error) {
            console.error('Unable to load class homework:', error);
            setErrorMessage('Unable to load your teaching-group homework right now. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    }, [today]);

    const applySchoolData = useCallback(async (uid, data, requestedBoardId = '') => {
        const resolvedBoardId = data.board?.table_id || requestedBoardId;
        const dashboardRows = data.subjects || [];
        const catalog = uniqueSubjectCatalog(dashboardRows);
        const availableGroups = new Map(catalog.map((item) => [
            item.subject,
            new Set((item.class_tags || []).map((group) => group.public_token)),
        ]));
        const selections = Object.fromEntries(Object.entries(getSubjectSelections(resolvedBoardId))
            .filter(([subject, group]) => availableGroups.get(subject)?.has(group.public_token)));
        replaceSubjectSelections(selections, resolvedBoardId);
        setStoredSchool(uid);
        setStoredBoard(resolvedBoardId);
        setSchoolUid(uid);
        setSchoolName(data.organization?.name || 'School');
        setBoardId(resolvedBoardId);
        setBoardName(data.board?.name || '');
        setSubjectCatalog(catalog);
        setBoardRows(dashboardRows);
        setSubjectSelections(selections);
        setIsLoggedIn(true);
        const tokens = selectedGroupTokens(selections);
        if (tokens.length) {
            await fetchClassData(tokens, resolvedBoardId, dashboardRows, selections);
        } else {
            setHomeworkTable(buildClassHomeworkTable([], dashboardRows));
            setUpcomingEvents([]);
            setDeadlineItems([]);
            setActiveClasses([]);
        }
    }, [fetchClassData]);

    const fetchSchoolData = useCallback(async (uid, targetBoardId = '') => {
        setIsLoading(true);
        setErrorMessage('');
        try {
            const data = await fetchSubjectGroups(uid, targetBoardId);
            await applySchoolData(uid, data, targetBoardId);
        } catch (error) {
            console.error('Unable to load subject groups:', error);
            setErrorMessage(
                [404, 409].includes(error.status)
                    ? error.message
                    : 'Unable to load the school teaching groups right now.',
            );
        } finally {
            setIsLoading(false);
        }
    }, [applySchoolData]);

    const fetchDeviceData = useCallback(async (device) => {
        setIsLoading(true);
        setErrorMessage('');
        try {
            const binding = await fetchDeviceSchool(device);
            if (!binding.is_bound || !binding.uid) {
                throw new Error('Device is not bound to a school.');
            }
            if (binding.subjectGroups) {
                await applySchoolData(
                    binding.uid,
                    binding.subjectGroups,
                    binding.subjectGroups.board?.table_id || '',
                );
            } else {
                await fetchSchoolData(binding.uid);
            }
        } catch (error) {
            console.error('Unable to resolve classroom device:', error);
            setErrorMessage(
                error.message.startsWith('Classroom device links are not enabled')
                    ? error.message
                    : 'This classroom device is not linked to a school. Ask a teacher or administrator to check its setup.',
            );
        } finally {
            setIsLoading(false);
        }
    }, [applySchoolData, fetchSchoolData]);

    // Check session storage on component mount
    useEffect(() => {
        const requestedClassTokens = new URLSearchParams(location.search).getAll('class');
        const requestedSchool = new URLSearchParams(location.search).get('school');
        const requestedDevice = new URLSearchParams(location.search).get('device');
        const requestedBoard = new URLSearchParams(location.search).get('board') || '';

        if (requestedSchool) {
            clearStoredClassTokens();
            localStorage.removeItem('name');
            fetchSchoolData(requestedSchool, requestedBoard);
        } else if (requestedDevice) {
            clearStoredClassTokens();
            localStorage.removeItem('name');
            fetchDeviceData(requestedDevice);
        } else if (requestedClassTokens.length) {
            clearStoredSchool();
            setSchoolUid('');
            setSchoolName('');
            setBoardId('');
            setBoardName('');
            setSubjectCatalog([]);
            setBoardRows([]);
            setSubjectSelections({});
            const savedClassTokens = addStoredClassTokens(requestedClassTokens);
            localStorage.removeItem('name');
            setIsLoggedIn(true);
            fetchClassData(savedClassTokens);
        } else if (name) {
            clearStoredSchool();
            setSchoolUid('');
            setSchoolName('');
            setBoardId('');
            setBoardName('');
            setSubjectCatalog([]);
            setBoardRows([]);
            setSubjectSelections({});
            localStorage.setItem('name', name);
            clearStoredClassTokens();
            setIsLoggedIn(true);
            fetchDashboardData(name);
        } else {
            // Check session storage if no name is in the URL
            const savedClassTokens = getStoredClassTokens();
            const savedUserName = localStorage.getItem('name');
            const savedSchool = getStoredSchool();
            const savedBoard = getStoredBoard();
            if (savedSchool) {
                fetchSchoolData(savedSchool, savedBoard);
            } else if (savedClassTokens.length) {
                setIsLoggedIn(true);
                fetchClassData(savedClassTokens);
            } else if (savedUserName) {
                setIsLoggedIn(true);
                fetchDashboardData(savedUserName);
            }
        }
        
    }, [fetchClassData, fetchDashboardData, fetchDeviceData, fetchSchoolData, location.search, name]);

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        if (homeworkUrl.trim()) {
            try{
                const url = new URL(homeworkUrl);
                const searchParams = new URLSearchParams(url.search);
                const extractedName = searchParams.get('name');
                const extractedClasses = getClassTokensFromUrl(url);
                const extractedSchool = url.searchParams.get('school');
                const extractedDevice = url.searchParams.get('device');
                const extractedBoard = url.searchParams.get('board') || '';
                if (extractedSchool) {
                    clearStoredClassTokens();
                    localStorage.removeItem('name');
                    setHomeworkUrl('');
                    fetchSchoolData(extractedSchool, extractedBoard);
                } else if (extractedDevice) {
                    clearStoredClassTokens();
                    localStorage.removeItem('name');
                    setHomeworkUrl('');
                    fetchDeviceData(extractedDevice);
                } else if (extractedClasses.length) {
                    clearStoredSchool();
                    setSchoolUid('');
                    setSubjectCatalog([]);
                    setBoardRows([]);
                    setBoardId('');
                    setBoardName('');
                    const savedClassTokens = addStoredClassTokens(extractedClasses);
                    localStorage.removeItem('name');
                    setIsLoggedIn(true);
                    setHomeworkUrl('');
                    fetchClassData(savedClassTokens);
                } else if (extractedName) {
                    clearStoredSchool();
                    setSchoolUid('');
                    setSubjectCatalog([]);
                    setBoardRows([]);
                    setBoardId('');
                    setBoardName('');
                    localStorage.setItem('name', extractedName);
                    clearStoredClassTokens();
                    setIsLoggedIn(true);
                    fetchDashboardData(extractedName);
                }
            }catch(e){
                console.log(e);
            }
        }
    };

    const handleSubjectSelection = (subject, publicToken) => {
        const catalogSubject = subjectCatalog.find((item) => item.subject === subject);
        const group = catalogSubject?.class_tags?.find((item) => item.public_token === publicToken);
        const selections = saveSubjectSelection(subject, group, boardId);
        setSubjectSelections(selections);
        const tokens = selectedGroupTokens(selections);
        const dashboardRows = boardRows.length
            ? boardRows
            : subjectCatalog.map((item) => item.subject);
        if (tokens.length) {
            fetchClassData(tokens, boardId, dashboardRows, selections);
        } else {
            setHomeworkTable(buildClassHomeworkTable([], dashboardRows));
            setUpcomingEvents([]);
            setDeadlineItems([]);
            setActiveClasses([]);
        }
    };

    const handleRemoveClass = (publicToken) => {
        const remainingTokens = removeStoredClassToken(publicToken);
        if (remainingTokens.length) {
            fetchClassData(remainingTokens);
        } else {
            setActiveClasses([]);
            setIsLoggedIn(false);
            setHomeworkTable({ tableData: undefined, maxCol: 0, maxRow: 0 });
            setUpcomingEvents([]);
            setDeadlineItems([]);
        }
    };

    const renderDashboard = () => (
        <div className="mx-auto flex h-full min-h-0 w-full max-w-6xl flex-col gap-[clamp(0.4rem,1.4dvh,1.25rem)]">
            <header className="mx-auto flex w-full max-w-4xl shrink-0 flex-col items-center gap-1 text-center">
                <h1 className="max-w-full break-words text-[clamp(1.75rem,4.2vw,3.5rem)] font-bold leading-none">
                    Student Dashboard
                </h1>
                <p className="dashboard-subtitle text-[clamp(0.875rem,2vw,1.35rem)] leading-tight text-gray-800">
                    {schoolUid
                        ? `${schoolName}${boardName ? ` · ${boardName}` : ''}`
                        : `Today is ${today}`}
                </p>
                {!schoolUid && activeClasses.length > 0 && (
                    <div className="mt-2 flex flex-wrap justify-center gap-2" aria-label="Saved teaching groups">
                        {activeClasses.map((classTag) => (
                            <span
                                key={classTag.tag_id}
                                className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-800"
                            >
                                {classTag.tag_name}
                                <button
                                    type="button"
                                    onClick={() => handleRemoveClass(classTag.public_token)}
                                    className="text-blue-600 hover:text-red-600 focus:outline-none"
                                    aria-label={`Remove ${classTag.tag_name}`}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </header>

            {schoolUid && activeModal === 'class-tags' && (
                <Modal title="Select your class tags" onClose={() => setActiveModal('')}>
                    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <p className="text-sm leading-6 text-gray-700">
                            Choose one tag for each subject. Homework updates immediately when you make a choice.
                        </p>
                        <span className="w-fit shrink-0 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-800 shadow-sm">
                            Saved on this device
                        </span>
                    </div>
                    {subjectCatalog.length ? (
                        <div className="grid gap-4 md:grid-cols-2">
                            {subjectCatalog.map((subjectItem) => (
                                <fieldset key={subjectItem.subject} className="rounded-lg border border-blue-200 bg-white p-4">
                                    <legend className="px-1 font-semibold text-gray-950">{subjectItem.subject}</legend>
                                    <div className="mt-2 grid gap-2">
                                        <label className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 ${
                                            !subjectSelections[subjectItem.subject]
                                                ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                                                : 'border-gray-200 hover:bg-blue-50'
                                        }`}>
                                            <input
                                                type="radio"
                                                name={`subject-${subjectItem.subject}`}
                                                value=""
                                                checked={!subjectSelections[subjectItem.subject]}
                                                onChange={() => handleSubjectSelection(subjectItem.subject, '')}
                                            />
                                            <span className="text-gray-600">No tag selected</span>
                                        </label>
                                        {(subjectItem.class_tags || []).map((group) => {
                                            const selected = subjectSelections[subjectItem.subject]?.public_token === group.public_token;
                                            return (
                                                <label
                                                    key={group.tag_id}
                                                    className={`flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2 ${
                                                        selected
                                                            ? 'border-blue-500 bg-blue-50 font-semibold ring-1 ring-blue-500'
                                                            : 'border-gray-200 hover:bg-blue-50'
                                                    }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name={`subject-${subjectItem.subject}`}
                                                        value={group.public_token}
                                                        checked={selected}
                                                        onChange={() => handleSubjectSelection(subjectItem.subject, group.public_token)}
                                                    />
                                                    <span>{group.tag_name}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </fieldset>
                            ))}
                        </div>
                    ) : (
                        <p className="rounded-lg border border-blue-200 bg-white px-4 py-3 text-sm text-gray-700">
                            This school has no active class tags yet. Ask a teacher to create a tag for a subject.
                        </p>
                    )}
                </Modal>
            )}

            {!schoolUid && activeClasses.length > 0 && (
                <form onSubmit={handleSubmit} className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3 sm:flex-row sm:items-end">
                    <label className="flex-1 text-sm font-medium text-gray-700">
                        Add another teaching-group link
                        <input
                            type="text"
                            value={homeworkUrl}
                            onChange={(e) => setHomeworkUrl(e.target.value)}
                            placeholder="Paste another homework QR link"
                            className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-base shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
                        />
                    </label>
                    <button
                        type="submit"
                        className="rounded-md bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        Add group
                    </button>
                </form>
            )}

            <div className="grid shrink-0 grid-cols-2 gap-[clamp(0.5rem,1.5vw,1rem)]">
                <SummaryTile
                    label="Upcoming Events"
                    value={isLoading ? '-' : upcomingEvents.length}
                    helper="Events and reminders coming up next."
                    tone="emerald"
                    onClick={() => setActiveModal('upcoming')}
                />
                <SummaryTile
                    label="Due Today"
                    value={isLoading ? '-' : deadlineItems.length}
                    helper="Homework with today's deadline."
                    tone="amber"
                    onClick={() => setActiveModal('deadlines')}
                />
            </div>

            <DashboardSection
                title="Today's Homework"
                action={
                    <div className="flex items-center gap-2">
                        {schoolUid && (
                            <button
                                type="button"
                                onClick={() => setActiveModal('class-tags')}
                                className="inline-flex rounded-md bg-blue-600 px-[clamp(0.6rem,1.5vw,1rem)] py-2 text-[clamp(0.75rem,1.4vw,0.875rem)] font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                aria-label={`tag (${selectedSubjectCount}/${subjectCatalog.length} selected)`}
                            >
                                tag
                            </button>
                        )}
                        <Link
                            to="/history"
                            className="inline-flex whitespace-nowrap rounded-md border border-gray-300 bg-white px-[clamp(0.6rem,1.5vw,1rem)] py-2 text-[clamp(0.75rem,1.4vw,0.875rem)] font-semibold text-gray-800 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            View History
                        </Link>
                    </div>
                }
            >
                <HomeworkTable
                    tableData={homeworkTable.tableData}
                    maxRow={homeworkTable.maxRow}
                    maxCol={homeworkTable.maxCol}
                    fitToWidth
                    fillAvailableHeight
                    dateLabel={formatBoardDate(today, language)}
                    dateTime={today}
                    subjectTags={homeworkTable.subjectTags}
                    subjectTagSelections={schoolUid ? subjectSelections : undefined}
                />
            </DashboardSection>

            {activeModal === 'upcoming' && (
                <Modal title="Upcoming Events" onClose={() => setActiveModal('')}>
                    <ItemList
                        items={upcomingEvents}
                        emptyMessage={isLoading ? 'Loading upcoming events...' : 'No upcoming events found.'}
                        showRecordDate
                    />
                </Modal>
            )}

            {activeModal === 'deadlines' && (
                <Modal title="Due Today" onClose={() => setActiveModal('')}>
                    <ItemList
                        items={deadlineItems}
                        emptyMessage={isLoading ? 'Loading deadlines...' : 'No deadlines due today.'}
                    />
                </Modal>
            )}
        </div>
    );

    return (
        <AppLayout fitViewport={isLoggedIn}>
        {isLoggedIn ? (
            renderDashboard()
        ) : (
            <div className="flex min-h-[calc(100vh-6rem)] w-full items-center justify-center">
                <form onSubmit={handleSubmit} className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                    <h1 className="mb-5 text-2xl font-bold">Homework Section</h1>
                    <p className="mb-4 text-sm leading-6 text-gray-700">
                        Scan the QR code shown in your classroom, or paste its link below. A school link will open the class-tag selector.
                    </p>
                    <label className="block text-base font-medium text-gray-700">
                        Homework Section URL:
                        <input
                        type="text"
                        value={homeworkUrl}
                        onChange={(e) => setHomeworkUrl(e.target.value)}
                        placeholder="Paste the homework QR link"
                        className="mt-2 block w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-base shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
                        />
                    </label>
                    <button
                        type="submit"
                        className="mt-5 w-full rounded-md bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        Save
                    </button>
                </form>
            </div>
        )}
        {errorMessage && (
            <Modal title="Unable to open homework" onClose={() => setErrorMessage('')}>
                <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800">
                    <p className="font-semibold">{errorMessage}</p>
                </div>
            </Modal>
        )}
        </AppLayout>
    );
}

export default Home;
